import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #selectionGeneration = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#selectionGeneration++;
    this.#controller.abort();
  }

  /**
   * Previews a swatch immediately, then reconciles the server-selected gallery.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const generation = ++this.#selectionGeneration;
    const selectedSwatch = event.target.querySelector('.atelier-photo-option input:checked');
    const previewId = selectedSwatch?.dataset.galleryMediaId;
    const hasPreview = this.presentation === 'carousel' && this.slideshow && previewId &&
      Array.from(this.slideshow.querySelectorAll('slideshow-slide')).some(
        (slide) => slide.getAttribute('slide-id') === previewId
      );

    if (hasPreview) {
      this.slideshow.select({ id: previewId }, undefined, { preserveOrder: true });
    }

    event.promise
      .then(({ detail }) => {
        if (generation !== this.#selectionGeneration || !this.isConnected || !detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const nextSlideshow = newMediaGallery.querySelector('slideshow-component');
        const nextSlides = Array.from(nextSlideshow?.querySelectorAll('slideshow-slide') ?? []);
        const currentSlides = Array.from(this.slideshow?.querySelectorAll('slideshow-slide') ?? []);
        const selectedIndex = Number(nextSlideshow?.getAttribute('initial-slide') ?? 0);
        const selectedId = nextSlides[selectedIndex]?.getAttribute('slide-id');
        const sameOrder = nextSlides.length === currentSlides.length && nextSlides.every(
          (slide, index) => slide.getAttribute('slide-id') === currentSlides[index]?.getAttribute('slide-id')
        );

        if (this.presentation === 'carousel' && this.slideshow && selectedId && sameOrder) {
          // Do not replay the preview when the response arrives or interrupt manual browsing.
          if (!hasPreview || selectedId !== previewId) {
            this.slideshow.select({ id: selectedId }, undefined, { preserveOrder: true });
          }
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
